import { IExpression, parse, PathSegment, Invocation } from './index';

export interface CompletionItem {
    label: string;
    kind: CompletionItemKind;
    detail?: string;
    documentation?: string;
    insertText?: string;
    range?: { start: number; end: number };
    triggerSuggest?: boolean;
}

export enum CompletionItemKind {
    Function = 'function',
    Operator = 'operator',
    Keyword = 'keyword',
    Property = 'property',
    Value = 'value',
    Method = 'method',
    Field = 'field',
    Variable = 'variable',
}

export interface CompletionContext {
    expression: string;
    position: number;
    word: string;
    lastChar: string;
    isInFunctionCall: boolean;
    functionName?: string;
    parenDepth: number;
    expectsOperator: boolean;
    expectsValue: boolean;
}

export const functionCompletions: CompletionItem[] = [
    {
        label: 'contains',
        kind: CompletionItemKind.Function,
        detail: 'Check if string/array contains value',
        insertText: 'contains(${1})',
        triggerSuggest: true,
    },
    {
        label: 'departmentIds',
        kind: CompletionItemKind.Function,
        detail: 'Get department IDs from job',
        insertText: 'departmentIds',
    },
    {
        label: 'mainDepartment',
        kind: CompletionItemKind.Function,
        detail: 'Get main department ID',
        insertText: 'mainDepartment',
    },
    {
        label: 'impliedDepartment',
        kind: CompletionItemKind.Function,
        detail: 'Get implied department ID',
        insertText: 'impliedDepartment',
    },
    {
        label: 'responsible',
        kind: CompletionItemKind.Function,
        detail: 'Get responsible ID from main department',
        insertText: 'responsible',
    },
    {
        label: 'impliedResponsible',
        kind: CompletionItemKind.Function,
        detail: 'Get responsible ID from implied department',
        insertText: 'impliedResponsible',
    },
    {
        label: 'getObliqueDepartment',
        kind: CompletionItemKind.Function,
        detail: 'Get oblique department ID',
        insertText: 'getObliqueDepartment',
    },
    {
        label: 'getProductiveDepartment',
        kind: CompletionItemKind.Function,
        detail: 'Get productive department ID',
        insertText: 'getProductiveDepartment',
    },
    {
        label: 'anyDepartment',
        kind: CompletionItemKind.Function,
        detail: 'Check if identity has any of departments',
        insertText: 'anyDepartment(${1})',
        triggerSuggest: true,
    },
];

const operatorCompletions: CompletionItem[] = [
    { label: '&&', kind: CompletionItemKind.Operator, detail: 'Logical AND' },
    { label: '||', kind: CompletionItemKind.Operator, detail: 'Logical OR' },
    { label: '==', kind: CompletionItemKind.Operator, detail: 'Equals' },
    { label: '!=', kind: CompletionItemKind.Operator, detail: 'Not equals' },
    { label: '<', kind: CompletionItemKind.Operator, detail: 'Less than' },
    { label: '>', kind: CompletionItemKind.Operator, detail: 'Greater than' },
    { label: '<=', kind: CompletionItemKind.Operator, detail: 'Less than or equal' },
    { label: '>=', kind: CompletionItemKind.Operator, detail: 'Greater than or equal' },
    { label: '+', kind: CompletionItemKind.Operator, detail: 'Addition' },
    { label: '-', kind: CompletionItemKind.Operator, detail: 'Subtraction' },
    { label: '*', kind: CompletionItemKind.Operator, detail: 'Multiplication' },
    { label: '/', kind: CompletionItemKind.Operator, detail: 'Division' },
];

export const logicalOperatorCompletions: CompletionItem[] = [
    { label: '&&', kind: CompletionItemKind.Operator, detail: 'Logical AND' },
    { label: '||', kind: CompletionItemKind.Operator, detail: 'Logical OR' },
];

export const comparisonOperatorCompletions: CompletionItem[] = [
    { label: '==', kind: CompletionItemKind.Operator, detail: 'Equals' },
    { label: '!=', kind: CompletionItemKind.Operator, detail: 'Not equals' },
    { label: '<', kind: CompletionItemKind.Operator, detail: 'Less than' },
    { label: '>', kind: CompletionItemKind.Operator, detail: 'Greater than' },
    { label: '<=', kind: CompletionItemKind.Operator, detail: 'Less than or equal' },
    { label: '>=', kind: CompletionItemKind.Operator, detail: 'Greater than or equal' },
];

const propertyCompletions: CompletionItem[] = [
    { label: 'identity', kind: CompletionItemKind.Variable, detail: 'Current user identity' },
    { label: 'work', kind: CompletionItemKind.Variable, detail: 'Current work context' },
    { label: 'job', kind: CompletionItemKind.Variable, detail: 'Current job' },
];

const identityPropertyCompletions: CompletionItem[] = [
    { label: 'departments', kind: CompletionItemKind.Property, detail: 'User departments' },
    { label: 'roles', kind: CompletionItemKind.Property, detail: 'User roles' },
    { label: 'policies', kind: CompletionItemKind.Property, detail: 'User policies' },
    { label: 'workerIds', kind: CompletionItemKind.Property, detail: 'Worker IDs' },
    { label: 'contractorIds', kind: CompletionItemKind.Property, detail: 'Contractor IDs' },
];

const identityMethodCompletions: CompletionItem[] = [
    { label: 'GetObliqueDepartments', kind: CompletionItemKind.Method, detail: 'Get oblique departments', insertText: 'GetObliqueDepartments' },
    { label: 'GetProductiveDepartments', kind: CompletionItemKind.Method, detail: 'Get productive departments', insertText: 'GetProductiveDepartments' },
    { label: 'HasDepartment', kind: CompletionItemKind.Method, detail: 'Check department membership', insertText: 'HasDepartment(${1})', triggerSuggest: true },
    { label: 'HasPolicy', kind: CompletionItemKind.Method, detail: 'Check policy', insertText: 'HasPolicy(${1})', triggerSuggest: true },
    { label: 'HasWorker', kind: CompletionItemKind.Method, detail: 'Check worker ID', insertText: 'HasWorker(${1})', triggerSuggest: true },
    { label: 'IsWorker', kind: CompletionItemKind.Method, detail: 'Check if worker role', insertText: 'IsWorker' },
    { label: 'IsContractor', kind: CompletionItemKind.Method, detail: 'Check if contractor role', insertText: 'IsContractor' },
    { label: 'IsGestor', kind: CompletionItemKind.Method, detail: 'Check if gestor role', insertText: 'IsGestor' },
];

const jobPropertyCompletions: CompletionItem[] = [
    { label: 'departments', kind: CompletionItemKind.Property, detail: 'Job departments' },
    { label: 'contractors', kind: CompletionItemKind.Property, detail: 'Job contractors' },
    { label: 'machineries', kind: CompletionItemKind.Property, detail: 'Job machineries' },
    { label: 'workers', kind: CompletionItemKind.Property, detail: 'Job workers' },
];

const jobMethodCompletions: CompletionItem[] = [
    { label: 'GetBooleanProperty', kind: CompletionItemKind.Method, detail: 'Get boolean property', insertText: 'GetBooleanProperty("${1}")', triggerSuggest: true },
    { label: 'GetStringProperty', kind: CompletionItemKind.Method, detail: 'Get string property', insertText: 'GetStringProperty("${1}")', triggerSuggest: true },
    { label: 'GetNumberProperty', kind: CompletionItemKind.Method, detail: 'Get number property', insertText: 'GetNumberProperty("${1}")', triggerSuggest: true },
];

const workPropertyCompletions: CompletionItem[] = [
    { label: 'departments', kind: CompletionItemKind.Property, detail: 'Work departments' },
    { label: 'jobs', kind: CompletionItemKind.Property, detail: 'Work jobs' },
];

const workMethodCompletions: CompletionItem[] = [
    { label: 'HasModule', kind: CompletionItemKind.Method, detail: 'Check module', insertText: 'HasModule("${1}")', triggerSuggest: true },
];

export function analyzeContext(expression: string, position: number): CompletionContext {
    const textBeforeCursor = expression.substring(0, position);
    const textAfterCursor = expression.substring(position);

    // Get the word being typed
    const wordMatch = textBeforeCursor.match(/[\w.]+$/);
    const word = wordMatch ? wordMatch[0] : '';

    // Get last non-whitespace character
    const trimmedBefore = textBeforeCursor.trim();
    const lastChar = trimmedBefore.length > 0 ? trimmedBefore.slice(-1) : '';

    // Check if in function call
    const parenOpenCount = (textBeforeCursor.match(/\(/g) || []).length;
    const parenCloseCount = (textBeforeCursor.match(/\)/g) || []).length;
    const parenDepth = Math.max(0, parenOpenCount - parenCloseCount);
    const isInFunctionCall = parenDepth > 0;

    // Find function name if in call
    let functionName: string | undefined;
    if (isInFunctionCall) {
        const funcMatch = textBeforeCursor.match(/(\w+)\([^)]*$/);
        functionName = funcMatch ? funcMatch[1] : undefined;
    }

    // Determine what's expected next
    const expectsOperator = [' ', ')', '"', "'"].includes(lastChar) || /\d$/.test(trimmedBefore);
    const expectsValue = ['(', ' ', '>', '<', '=', '!', ',', '&', '|'].includes(lastChar) || trimmedBefore.length === 0;

    return {
        expression,
        position,
        word,
        lastChar,
        isInFunctionCall,
        functionName,
        parenDepth,
        expectsOperator,
        expectsValue,
    };
}

export function getCompletions(expression: string, position: number): CompletionItem[] {
    const ctx = analyzeContext(expression, position);

    // Empty expression or starting fresh - properties first, then functions
    if (!ctx.word && !ctx.lastChar) {
        return [...propertyCompletions, ...functionCompletions];
    }

    // TYPING AFTER DOT - property/method access (e.g., "identity.")
    if (ctx.lastChar === '.') {
        const baseObject = ctx.word.replace(/\.$/, '');
        return getPropertyCompletions(baseObject);
    }

    // After a complete expression, suggest logical operators
    if (ctx.expectsOperator && !ctx.isInFunctionCall && !ctx.word) {
        return logicalOperatorCompletions;
    }

    // After comparison operator, suggest properties and functions
    if (['=', '!', '<', '>'].includes(ctx.lastChar)) {
        return [...propertyCompletions, ...functionCompletions];
    }

    // Inside function call - suggest properties and functions
    if (ctx.isInFunctionCall) {
        const afterLastComma = ctx.expression.substring(0, position).split(',').pop() || '';
        const trimmedAfterComma = afterLastComma.trim();

        // If just started typing argument
        if (!trimmedAfterComma || trimmedAfterComma.match(/^\s*$/)) {
            return [...propertyCompletions, ...functionCompletions];
        }

        // Filter by what's being typed
        return filterCompletions([...propertyCompletions, ...functionCompletions], trimmedAfterComma);
    }

    // Typing a word - filter completions (VS Code behavior: always show filtered list)
    if (ctx.word) {
        // Check if typing property path like "identity.dept"
        const dotIndex = ctx.word.lastIndexOf('.');
        if (dotIndex > 0) {
            const baseObject = ctx.word.substring(0, dotIndex);
            const propWord = ctx.word.substring(dotIndex + 1);
            const baseCompletions = getPropertyCompletions(baseObject);
            return filterCompletions(baseCompletions, propWord);
        }

        // Regular filtering - VS Code style: properties first, then functions, then operators
        const allCompletions = [
            ...propertyCompletions,
            ...functionCompletions,
            ...operatorCompletions,
        ];
        return filterCompletions(allCompletions, ctx.word);
    }

    // Default: show properties first, then functions
    return [...propertyCompletions, ...functionCompletions];
}

function filterCompletions(items: CompletionItem[], word: string): CompletionItem[] {
    if (!word) return items;

    const lowerWord = word.toLowerCase();
    return items.filter(item =>
        item.label.toLowerCase().includes(lowerWord)
    );
}

function getPropertyCompletions(baseObject: string): CompletionItem[] {
    switch (baseObject.toLowerCase()) {
        case 'identity':
            return [...identityPropertyCompletions, ...identityMethodCompletions];
        case 'job':
            return [...jobPropertyCompletions, ...jobMethodCompletions];
        case 'work':
            return [...workPropertyCompletions, ...workMethodCompletions];
        default:
            return [];
    }
}

export function getSignatureHelp(expression: string, position: number): SignatureHelp | null {
    const textBeforeCursor = expression.substring(0, position);

    // Find the function being called
    const match = textBeforeCursor.match(/(\w+)\(([^)]*)$/);
    if (!match) {
        return null;
    }

    const functionName = match[1];
    const argsText = match[2];

    // Count arguments (simple comma counting)
    let currentParameter = 0;
    if (argsText) {
        currentParameter = (argsText.match(/,/g) || []).length;
    }

    const signature = getFunctionSignature(functionName);
    if (!signature) {
        return null;
    }

    return {
        signatures: [{
            label: signature,
            parameters: getFunctionParameters(signature),
        }],
        activeSignature: 0,
        activeParameter: currentParameter,
    };
}

export interface SignatureHelp {
    signatures: SignatureInformation[];
    activeSignature: number;
    activeParameter: number;
}

export interface SignatureInformation {
    label: string;
    parameters: ParameterInformation[];
}

export interface ParameterInformation {
    label: string;
    documentation?: string;
}

function getFunctionSignature(name: string): string | null {
    const signatures: Record<string, string> = {
        'contains': 'contains(value)',
        'hasDepartment': 'hasDepartment(departmentId)',
        'anyDepartment': 'anyDepartment([departmentIds])',
        'hasPolicy': 'hasPolicy(policy)',
        'hasWorker': 'hasWorker(workerId)',
        'mainDepartment': 'mainDepartment()',
        'impliedDepartment': 'impliedDepartment()',
        'responsible': 'responsible()',
        'impliedResponsible': 'impliedResponsible()',
        'getObliqueDepartment': 'getObliqueDepartment()',
        'getProductiveDepartment': 'getProductiveDepartment()',
        'isWorker': 'isWorker()',
        'isContractor': 'isContractor()',
        'isGestor': 'isGestor()',
        'departmentIds': 'departmentIds()',
        'list': 'list()',
    };
    return signatures[name.toLowerCase()] || null;
}

function getFunctionParameters(signature: string): ParameterInformation[] {
    const match = signature.match(/\((.*)\)/);
    if (!match || !match[1]) {
        return [];
    }

    return match[1].split(',').map(p => ({
        label: p.trim(),
    }));
}

export function validateExpression(expression: string): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!expression || expression.trim().length === 0) {
        errors.push('Expression cannot be empty');
        return { isValid: false, errors, warnings };
    }

    // Check for balanced parentheses
    const openParens = (expression.match(/\(/g) || []).length;
    const closeParens = (expression.match(/\)/g) || []).length;

    if (openParens !== closeParens) {
        errors.push(`Unbalanced parentheses: ${openParens} open, ${closeParens} closed`);
    }

    // Try to parse
    try {
        parse(expression);
    } catch (e: any) {
        errors.push(`Parse error: ${e.message || e.toString()}`);
    }

    // Check for common mistakes
    if (expression.includes('  ')) {
        warnings.push('Multiple consecutive spaces detected');
    }

    if (expression.match(/\s+(AND|OR)\s+/i) && !expression.match(/\s+(AND|OR)\s+/)) {
        warnings.push('Logical operators should be uppercase');
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
}

export function formatExpression(expression: string): string {
    // Add spaces around operators for better readability
    let formatted = expression;

    formatted = formatted.replace(/\s*(==|!=|<=|>=|<|>)\s*/g, ' $1 ');
    formatted = formatted.replace(/\s*(AND|OR)\s*/g, ' $1 ');
    formatted = formatted.replace(/\s*\(\s*/g, '(');
    formatted = formatted.replace(/\s*\)\s*/g, ')');
    formatted = formatted.replace(/\s*,\s*/g, ', ');

    return formatted.trim();
}
